import {
  CASE_FILE_KIND,
  CASE_MEDICAL_FIELDS,
  CaseMedicalField,
  CaseModel,
  LOW_CONFIDENCE_CHECK,
  MEDICAL_FIELD_VERIFICATION_METHOD,
} from "@shared/models";

interface CaseDocumentInfo {
  title: string;
  /** In sentence case, e.g. "Medical summaries removed" */
  name: string;
  menuName: string;
  /** Under the title of the document's card */
  cardLabel: string;
  multipleFiles: boolean;
  description?: string;
  addLabel?: string;
}

export const CASE_DOCUMENT_KINDS = [CASE_FILE_KIND.PSR, CASE_FILE_KIND.MEDICAL, CASE_FILE_KIND.NOTES];

export const CASE_DOCUMENT_INFO: Record<CASE_FILE_KIND, CaseDocumentInfo> = {
  [CASE_FILE_KIND.PSR]: {
    title: "Presentence Report",
    name: "PSR",
    menuName: "PSR",
    cardLabel: "Required",
    multipleFiles: false,
  },
  [CASE_FILE_KIND.MEDICAL]: {
    title: "Medical Summaries",
    name: "Medical summaries",
    menuName: "Medical Summaries",
    cardLabel: "Optional",
    multipleFiles: true,
    description:
      "Prepared medical summaries, not full medical records. PDF, DOCX, TXT, JPG or PNG. Up to 10 files, 12 pages and 20 MB.",
    addLabel: "Add medical summaries",
  },
  [CASE_FILE_KIND.NOTES]: {
    title: "Case Notes",
    name: "Case notes",
    menuName: "Case Notes",
    cardLabel: "Optional · not scored",
    multipleFiles: false,
    description:
      "Prisonology’s notes from conversations with or about the case subject. PDF, DOCX or TXT. Saved as written; not used in scoring.",
    addLabel: "Add case notes",
  },
};

const MB = 1024 * 1024;

export interface CaseDocumentLimits {
  /** Allowed file extensions with the MIME type each is uploaded as */
  formats: Record<string, string>;
  formatNames: string[];
  /** Refused extensions that have their own message */
  formatErrors?: Record<string, string>;
  maxFileSize: number;
  maxTotalSize: number;
  /** Left out when the document has no page limit */
  maxPages?: number;
  maxFiles: number;
}

const HEIC_ERROR = "HEIC isn't supported. Export it as JPG or PNG.";

export const CASE_DOCUMENT_LIMITS = {
  [CASE_FILE_KIND.MEDICAL]: {
    formats: {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      txt: "text/plain",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
    },
    formatNames: ["PDF", "DOCX", "TXT", "JPG", "PNG"],
    formatErrors: { heic: HEIC_ERROR, heif: HEIC_ERROR },
    maxFileSize: 10 * MB,
    maxTotalSize: 20 * MB,
    maxPages: 12,
    maxFiles: 10,
  },
  [CASE_FILE_KIND.NOTES]: {
    formats: {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      txt: "text/plain",
    },
    formatNames: ["PDF", "DOCX", "TXT"],
    maxFileSize: 10 * MB,
    maxTotalSize: 10 * MB,
    maxFiles: 1,
  },
} satisfies Partial<Record<CASE_FILE_KIND, CaseDocumentLimits>>;

/** Document types users upload themselves. */
export type UploadedCaseFileKind = keyof typeof CASE_DOCUMENT_LIMITS;

/** Form fields that parsing medical summaries or case notes saves to. */
export const CASE_DOCUMENT_FORM_FIELDS: (CaseMedicalField | "case_notes")[] = [...CASE_MEDICAL_FIELDS, "case_notes"];

/** What a case takes from the refetched case when a document is parsed, leaving the rest as is. */
export const CASE_DOCUMENT_SYNC_KEYS: (keyof CaseModel)[] = [
  ...CASE_DOCUMENT_FORM_FIELDS,
  "field_sources",
  "pending_choices",
  "messages",
];

export const MEDICAL_FIELD_LABELS: Record<CaseMedicalField, string> = {
  medical_history_notes: "Medical History",
  mental_health_notes: "Mental Health Notes",
  drug_alcohol_notes: "Drug & Alcohol History",
  conditions: "Conditions",
  diagnoses: "Diagnoses",
  mobility_flags: "Mobility Flags",
  specialist_needs: "Specialist Needs",
};

/** In the order the fields are numbered on the form. */
export const MEDICAL_FIELDS_FORM_ORDER: CaseMedicalField[] = [
  "mental_health_notes",
  "medical_history_notes",
  "drug_alcohol_notes",
  "conditions",
  "diagnoses",
  "mobility_flags",
  "specialist_needs",
];

export const MITIGATING_FACTORS_SECTION_ID = "mitigating-factors";

export enum CASE_DOCUMENT_MODAL_STAGE {
  UPLOAD = "upload",
  /** Parsing progress, then the result */
  STATUS = "status",
}

export const LOW_CONFIDENCE_CHECK_NAMES: Record<LOW_CONFIDENCE_CHECK, string> = {
  [LOW_CONFIDENCE_CHECK.NOT_FOUND]: "Not found in source",
  [LOW_CONFIDENCE_CHECK.NOT_SUPPORTED]: "Not supported by source",
  [LOW_CONFIDENCE_CHECK.HARD_TO_READ]: "Hard to read",
  [LOW_CONFIDENCE_CHECK.FILES_DISAGREE]: "Files disagree",
};

export const VERIFICATION_METHOD_LABELS: Record<MEDICAL_FIELD_VERIFICATION_METHOD, string> = {
  [MEDICAL_FIELD_VERIFICATION_METHOD.CHECKED]: "checked against source",
  [MEDICAL_FIELD_VERIFICATION_METHOD.EDITED]: "edited",
};
